import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { DocTimelineUpdate } from '@common/collab/types/doc-timeline-update.type';
import { CollabSessionOptions } from '@common/sockets/collab-session';
import { cache } from '@common/util/cache.operator';
import { StringService } from '@portal-core/general/services/string.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { FlareXmlEditorComponent } from '@portal-core/project-files/components/flare-xml-editor/flare-xml-editor.component';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { FlareFileTextEditorService } from '@portal-core/project-files/services/flare-file-text-editor.service';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { FlareTextEditorType } from '@portal-core/project-files/types/flare-text-editor-type.type';
import { FlareXmlEditorMode } from '@portal-core/project-files/types/flare-xml-editor-mode.type';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { Base64 } from 'js-base64';
import { Observable, combineLatest, filter, map, tap } from 'rxjs';

@Component({
  selector: 'mc-project-flare-file',
  templateUrl: './project-flare-file.component.html',
  styleUrls: ['./project-flare-file.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectFlareFileComponent implements OnInit {
  @Input() branchName: string;
  @Input() editorType?: FlareTextEditorType = 'solo';
  @Input() getTimelineDocVersion?: CollabSessionOptions['getTimelineDocVersion'];
  @Input() licenseUser: LicenseUser;
  @Input() mode?: FlareXmlEditorMode;
  @Input() projectFile: ProjectFile;
  @Input() projectId: number;
  @Input() projectLanguage: string;
  @Input() readonly: boolean;
  @Input() showConditions?: boolean;
  @Input() timelineEnabled?: boolean = false;
  @Input() trackedChanges?: boolean;
  @Input() wrapLines?: boolean;

  @Output() contentChange: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() docCommit: EventEmitter<CollabDocCommitEvent> = new EventEmitter<CollabDocCommitEvent>();
  @Output() docRestore: EventEmitter<CollabDocRestoreEvent> = new EventEmitter<CollabDocRestoreEvent>();
  @Output() load: EventEmitter<void> = new EventEmitter<void>();
  @Output() modeChange: EventEmitter<FlareXmlEditorMode> = new EventEmitter<FlareXmlEditorMode>();
  @Output() navToFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() showConditionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() timelineUpdate: EventEmitter<DocTimelineUpdate> = new EventEmitter<DocTimelineUpdate>();
  @Output() trackedChangesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() unload: EventEmitter<void> = new EventEmitter<void>();
  @Output() usersUpdate: EventEmitter<CollabUserJSON[]> = new EventEmitter<CollabUserJSON[]>();
  @Output() wrapLinesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(FlareXmlEditorComponent, { static: true }) flareXmlEditor: FlareXmlEditorComponent;

  @InputObservable('mode') mode$: Observable<string>;
  @InputObservable(['editorType', 'projectFile']) contentInputs$: Observable<[FlareTextEditorType, ProjectFile]>;

  public get docId(): string {
    return this.flareXmlEditor?.docId;
  }

  public get docVersion(): number {
    return this.flareXmlEditor?.docVersion;
  }

  public get valid(): boolean {
    return this.flareXmlEditor.valid;
  }

  public get validated(): boolean {
    return this.flareXmlEditor.validated;
  }

  public get textValid(): boolean {
    return this.flareXmlEditor.textValid;
  }

  public get textValidated(): boolean {
    return this.flareXmlEditor.textValidated;
  }

  CollabFileType: typeof CollabFileType = CollabFileType;

  content$: Observable<string>;
  /** An observable for whether to show a message to the user that they can't edit the code. */
  showCannotEditCodeMessage$: Observable<boolean>;

  constructor(
    private stringService: StringService,
    private flareFileTextEditorService: FlareFileTextEditorService,
    private permissionsService: PermissionsService,
    private projectFilesService: ProjectFilesService) { }

  ngOnInit() {
    this.showCannotEditCodeMessage$ = combineLatest([
      this.mode$,
      this.permissionsService.currentUserHasPermission$(CentralPermissions.EditCode, this.projectId)
    ]).pipe(
      map(([mode, userCanEditCode]) => mode === 'code' && !userCanEditCode)
    );

    this.content$ = this.contentInputs$.pipe(
      filter(([editorType, projectFile]) => editorType === 'solo'),
      map(([editorType, projectFile]) => {
        if (projectFile) {
          return this.stringService.stripBOM(Base64.decode(projectFile.Content));
        }
      }),
      // It seems this 'tap' operator duplicates the functionality of the FlareXmlEditorComponent child component to switch to code mode when the content is invalid.
      tap(content => {
        // Validate the content to figure out if the mode should switch to code
        // Check file type to detect false positives when switching the editor from other file types.
        // If we didn't do this there would likely be a force switch to code editor because the other file type content would not be able to pass validation.
        if (content && this.projectFilesService.getFileType(this.projectFile.Path) === ProjectFileType.Flare) {
          // If in text mode and the content is invalid
          if (this.mode === 'text' && this.flareFileTextEditorService.validate(content)) {
            // Then switch to code mode since the ProseMirror editor can't do anything with invalid code
            this.mode = 'code';
            this.modeChange.emit(this.mode);
          }
        }
      }),
      cache()
    );

    // this.content$ = this.projectFile$.pipe(
    //   map(projectFile => {
    //     if (projectFile) {
    //       return this.stringService.stripBOM(Base64.decode(projectFile.Content));
    //     }
    //   }),
    //   // It seems this 'tap' operator duplicates the functionality of the FlareXmlEditorComponent child component to switch to code mode when the content is invalid.
    //   tap(content => {
    //     // Validate the content to figure out if the mode should switch to code
    //     // Check file type to detect false positives when switching the editor from other file types.
    //     // If we didn't do this there would likely be a force switch to code editor because the other file type content would not be able to pass validation.
    //     if (content && this.projectFilesService.getFileType(this.projectFile.Path) === ProjectFileType.Flare) {
    //       // If in text mode and the content is invalid
    //       if (this.mode === 'text' && this.flareFileTextEditorService.validate(content)) {
    //         // Then switch to code mode since the ProseMirror editor can't do anything with invalid code
    //         this.mode = 'code';
    //         this.modeChange.emit(this.mode);
    //       }
    //     }
    //   }),
    //   cache()
    // );
  }

  onModeChanged(mode: FlareXmlEditorMode) {
    if (mode !== this.mode) {
      this.mode = mode;
      this.modeChange.emit(this.mode);
    }
  }

  getDocVersion() {

  }
  canChangeMode(mode: FlareXmlEditorMode): boolean {
    return this.flareXmlEditor?.canChangeMode(mode);
  }

  getContent(): string {
    return this.flareXmlEditor?.getContent();
  }

  getFormattedContent(): string {
    return this.flareXmlEditor?.getFormattedContent();
  }

  validate() {
    this.flareXmlEditor.validate();
  }

  onWrapLinesChanged(wrapLines: boolean) {
    this.wrapLines = wrapLines;
    this.wrapLinesChange.emit(wrapLines);
  }

  onTrackedChangesChanged(trackedChanges: boolean) {
    this.trackedChanges = trackedChanges;
    this.trackedChangesChange.emit(trackedChanges);
  }

  onShowConditionsChanged(showConditions: boolean) {
    this.showConditions = showConditions;
    this.showConditionsChange.emit(showConditions);
  }
}
